import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { useUI } from '../../contexts/UIContext';
import { useBilling } from '../../contexts/BillingContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Individual hooks for data fetching
import { useTableColumns } from '../../hooks/useTableColumns';
import { useRefreshTableProfile } from '../../hooks/useTableProfile';
import { useTableProfiling } from '../../hooks/useMetadataTiers';
import { useTablesData } from '../../hooks/useTablesData';
import TableHeader from './components/TableHeader';
import TableTabs from './components/TableTabs';
import TableOverview from './components/TableOverview';
import TableInsights from './components/TableInsights';
import TableHistory from './components/TableHistory';
import TablePreview from './components/TablePreview';
import TableRelationships from './components/TableRelationships';

const TableDetailPage = () => {
  const { connectionSlug, tableName } = useParams();
  const navigate = useNavigate();
  const { connections, activeConnection, setCurrentConnection, getConnectionBySlug } = useConnection();
  const { updateBreadcrumbs, showNotification } = useUI();
  const { isFeatureEnabled, plan } = useBilling();

  // Check if tier 4 (profiling) is enabled for this plan
  const enabledTiers = plan?.features?.enabled_tiers || [1, 2];
  const tier4Enabled = enabledTiers.includes(4) || isFeatureEnabled('tier4_enabled');

  const [activeTab, setActiveTab] = useState('overview');
  const [connection, setConnection] = useState(null);
  const [loadedTabs, setLoadedTabs] = useState(new Set(['overview'])); // Track which tabs have been loaded

  // Lookup connection by slug and get connection ID for API calls
  const resolvedConnection = React.useMemo(() => {
    return getConnectionBySlug(connectionSlug);
  }, [connectionSlug, getConnectionBySlug]);

  const connectionId = resolvedConnection?.id;

  // Get table metadata to extract table ID
  const tablesQuery = useTablesData(connectionId, {
    enabled: !!(connectionId && tableName)
  });

  // Find the table ID from the table name
  const tableId = React.useMemo(() => {
    if (!tablesQuery.data?.tables) {
      console.log('[TableDetailPage] No tables data yet');
      return null;
    }
    // Match against full qualified name (database.schema.table)
    const table = tablesQuery.data.tables.find(t => {
      const fullName = `${t.database}.${t.schema}.${t.name}`;
      return fullName === tableName;
    });
    console.log('[TableDetailPage] Looking for table:', tableName);
    console.log('[TableDetailPage] Found table:', table);
    console.log('[TableDetailPage] Table ID:', table?._raw?.id);
    return table?._raw?.id || null;
  }, [tablesQuery.data, tableName]);

  // Individual hooks - only enable when tab is loaded
  const columnsQuery = useTableColumns(connectionId, tableName, {
    enabled: !!(connectionId && tableName && (loadedTabs.has('overview') || loadedTabs.has('insights')))
  });

  // Tier 4 profiling data - use tableId instead of tableName (single source of truth)
  const profilingQuery = useTableProfiling(connectionId, tableId, {
    enabled: !!(connectionId && tableId && (loadedTabs.has('overview') || loadedTabs.has('insights')))
  });


  // Profile refresh mutation
  const refreshProfileMutation = useRefreshTableProfile(connectionId, tableName);

  // Set the active connection based on connectionSlug from URL
  useEffect(() => {
    if (connectionSlug && connections.length > 0) {
      const conn = getConnectionBySlug(connectionSlug);
      if (conn) {
        setConnection(conn);
        if (!activeConnection || activeConnection.id !== conn.id) {
          setCurrentConnection(conn);
        }
      } else {
        showNotification('Connection not found', 'error');
        navigate('/explorer');
      }
    }
  }, [connectionSlug, connections, activeConnection, setCurrentConnection, navigate, showNotification, getConnectionBySlug]);

  // Set breadcrumbs
  useEffect(() => {
    updateBreadcrumbs([
      { name: 'Data Explorer', href: '/explorer' },
      { name: tableName }
    ]);
  }, [tableName, updateBreadcrumbs]);

  // Handle tab changes and lazy loading
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Add tab to loaded tabs set when it's first accessed
    setLoadedTabs(prev => new Set([...prev, tab]));
  };

  // Handle refresh profile using the mutation hook
  const handleRefreshProfile = async () => {
    if (!connection) return;

    try {
      showNotification('Refreshing profile...', 'info');
      await refreshProfileMutation.mutateAsync();
      showNotification('Profile refreshed successfully', 'success');
    } catch (error) {
      console.error(`Error refreshing profile for ${tableName}:`, error);
      showNotification(`Failed to refresh profile for ${tableName}`, 'error');
    }
  };


  return (
    <div className="py-4">
      {/* Table header */}
      <TableHeader
        tableName={tableName}
        connectionName={connection?.name}
        connectionId={connectionId}
        onRefreshProfile={handleRefreshProfile}
        isRefreshing={refreshProfileMutation.isPending}
        profilingEnabled={tier4Enabled}
      />

      {/* Tabs and content */}
      <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
        <TableTabs
          activeTab={activeTab}
          onChange={handleTabChange}
        />

        {connection ? (
          <div className="p-6">
            {activeTab === 'overview' && (
              <TableOverview
                columns={columnsQuery.data}
                profiling={profilingQuery.data}
                isLoading={columnsQuery.isLoading || profilingQuery.isLoading}
                error={columnsQuery.error || profilingQuery.error}
                tableName={tableName}
                connectionId={connectionId}
              />
            )}

            {activeTab === 'insights' && (
              <TableInsights
                tableName={tableName}
                connectionId={connectionId}
              />
            )}

            {activeTab === 'sample' && (
              <TablePreview
                connectionId={connectionId}
                tableName={tableName}
              />
            )}

            {activeTab === 'relationships' && (
              <TableRelationships
                tableName={tableName}
                connectionId={connectionId}
              />
            )}
          </div>
        ) : (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}
      </div>
    </div>
  );
};

export default TableDetailPage;